import { useCallback, useState } from "react";

import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { Datatable } from "@/domain/view/components/datatable/datatable";
import { DatatableRowDataWithCacheKey, defaultDatatableState } from "@/domain/view/components/datatable/datatable-types";
import type { Workspace } from "@/domain/workspace/models/workspace-model";
import { Row } from "@tanstack/react-table";

import type { DataRequestEntity, DataRequestFreeTextQuestion } from "../../models/data-request.model";

import { EntityResponseSheet } from "./entity-response-sheet";
import { COLUMN_ID_ENTITY_STATUS, useDataRequestEntityColumns } from "./use-data-request-entity-columns";

const VIEW_KEY = "data-request-entity-list";
const COLUMN_ID_ENTITY_TITLE = "entityTitle";

interface DataRequestEntityListProps {
  entityRequests: DataRequestEntity[];
  workspace: Workspace;
  fieldSelection?: string[] | null;
  enableDocuments?: boolean;
  freeTextQuestions?: DataRequestFreeTextQuestion[] | null;
}

export function DataRequestEntityList({
  entityRequests,
  workspace,
  fieldSelection,
  enableDocuments,
  freeTextQuestions,
}: DataRequestEntityListProps) {
  const { t } = useDomainTranslation("data-request");
  const columns = useDataRequestEntityColumns({ workspaceId: workspace.id });
  const [selectedEntity, setSelectedEntity] = useState<DataRequestEntity | null>(null);

  const handleRowClick = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<DataRequestEntity>>) => {
      const original = row.original;
      if ("id" in original) {
        setSelectedEntity(original);
      }
    },
    [],
  );

  if (entityRequests.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">{t("detail.noEntities")}</div>
    );
  }

  return (
    <>
      <div className="relative flex min-h-0 flex-1 items-stretch overflow-y-auto">
        <Datatable<DataRequestEntity>
          viewKey={VIEW_KEY}
          workspaceId={workspace.id}
          data={entityRequests}
          columns={columns}
          initialState={{
            ...defaultDatatableState,
            showColumnHeaders: true,
            sectionColumnId: COLUMN_ID_ENTITY_STATUS,
            sorting: [{ id: COLUMN_ID_ENTITY_TITLE, desc: false }],
          }}
          debugTable={false}
          synchToApi={false}
          initWithUrlParams={false}
          onRowClick={handleRowClick}
          config={{ quicksearch: true }}
          getColumnCanGlobalFilter={(column) => [COLUMN_ID_ENTITY_TITLE].includes(column.id)}
          globalFilterFn={(row, columnId, value) => {
            const normalizedValue = String(value ?? "").toLowerCase();
            if (normalizedValue.length === 0) return true;
            const original = row.original;
            if (!("entityId" in original)) return true;
            if (columnId === COLUMN_ID_ENTITY_TITLE) {
              const title = original.entityTitle ?? original.entityId;
              return title.toLowerCase().includes(normalizedValue);
            }
            return false;
          }}
        />
      </div>

      <EntityResponseSheet
        entity={selectedEntity}
        fieldSelection={fieldSelection}
        enableDocuments={enableDocuments}
        freeTextQuestions={freeTextQuestions}
        workspaceId={workspace.id}
        open={selectedEntity !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedEntity(null);
        }}
      />
    </>
  );
}
